import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { PlayerVerticalSliceView } from '@aztec-realms/shared-types';
import { useJourneySafety } from '../journey-safety';
import { useNetworkStatus } from '../network-status';
import { openDailyDecodeOnEntry, type DailyDecodeEntryResult } from './api-vertical-slice';
import { commandId, verticalSliceQueryKey } from './use-vertical-slice';

// This is only a request-suppression hint from a completed server response. It cannot grant
// a puzzle/reward. The server's deadline expires it, including across the next UTC day.
let completedDaily: { userId: string; availableAgainAt: string } | undefined;

export interface DailyDecodeEntryState {
  message: string;
  pending: boolean;
  error?: string;
}

const entryFailureMessage = 'Your Daily Codex could not open. Try again to continue.';

export function useDailyDecodeEntryState(
  snapshot: PlayerVerticalSliceView | undefined,
): DailyDecodeEntryState {
  const cache = useQueryClient();
  const online = useNetworkStatus();
  const journey = useJourneySafety();
  const latest = useRef({ snapshot, online, passive: journey.safety.mode === 'journey-passive' });
  useEffect(() => {
    latest.current = { snapshot, online, passive: journey.safety.mode === 'journey-passive' };
  }, [snapshot, online, journey.safety.mode]);
  const userId = snapshot?.profile.userId;
  const [entry, setEntry] = useState<
    { userId: string; runId: string; eligible: boolean } | undefined
  >(undefined);
  // Capture only account entry, not later board completion or background refreshes.
  if (userId !== entry?.userId) {
    setEntry(
      userId && snapshot
        ? {
            userId,
            runId: snapshot.decode.runId,
            eligible:
              snapshot.source === 'api' &&
              snapshot.decode.status === 'solved' &&
              !['weave_only', 'civic_only'].includes(snapshot.decode.rewardMode ?? 'standard'),
          }
        : undefined,
    );
  }
  const active = useRef(false);
  const attempt = useRef<Promise<DailyDecodeEntryResult> | undefined>(undefined);
  const attemptEntry = useRef(entry);
  const [resultMessage, setResultMessage] = useState<{
    entry: typeof entry;
    text: string;
    error?: string;
  }>();
  const completeMessage = (availableAgainAt: string) =>
    Number.isFinite(Date.parse(availableAgainAt))
      ? `Today’s Daily Codex is complete. The next glyph opens at ${new Date(availableAgainAt).toLocaleString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' })}.`
      : 'Daily Codex availability is temporarily unavailable. Reopen Decode to try again.';

  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  useEffect(() => {
    if (attemptEntry.current !== entry) {
      attemptEntry.current = entry;
      attempt.current = undefined;
    }
    const initial = entry;
    if (!initial?.eligible || !online || journey.safety.mode === 'journey-passive') return;
    const setMessage = (text: string, error?: string) =>
      setResultMessage({ entry: initial, text, error });
    const canContinue = () => {
      const now = latest.current;
      return (
        active.current &&
        now.online &&
        !now.passive &&
        now.snapshot?.profile.userId === initial.userId &&
        now.snapshot.decode.runId === initial.runId &&
        now.snapshot.decode.status === 'solved'
      );
    };
    if (!canContinue()) return;
    let listening = true;
    if (!attempt.current) {
      attempt.current = openDailyDecodeOnEntry({
        entryRunId: initial.runId,
        commandId: commandId('decode.daily.entry'),
        canContinue,
        completedUntil:
          completedDaily?.userId === initial.userId ? completedDaily.availableAgainAt : undefined,
      });
    }
    // StrictMode/re-renders subscribe to the same attempt rather than issuing another POST.
    void attempt.current
      .then((result) => {
        if (!listening || !canContinue()) return;
        if (result.kind === 'complete') {
          completedDaily = { userId: initial.userId, availableAgainAt: result.availableAgainAt };
          setMessage(completeMessage(result.availableAgainAt));
        } else if (result.kind === 'opened' || result.kind === 'resume') {
          setMessage('');
          cache.setQueryData<PlayerVerticalSliceView>(verticalSliceQueryKey, (previous) =>
            previous?.profile.userId === initial.userId && previous.decode.runId === initial.runId
              ? { ...previous, decode: result.decode }
              : previous,
          );
        } else
          setMessage(
            'The Daily Codex check was interrupted. Reopen Decode to try again.',
            entryFailureMessage,
          );
      })
      .catch((error: unknown) => {
        if (listening && canContinue())
          setMessage(
            error instanceof Error
              ? error.message
              : 'Daily Codex is temporarily unavailable. Reopen Decode to try again.',
            entryFailureMessage,
          );
      });
    return () => {
      listening = false;
    };
  }, [entry, snapshot, online, journey.safety.mode, cache]);
  if (!entry?.eligible || snapshot?.decode.runId !== entry.runId)
    return { message: '', pending: false };
  if (!online) return { message: 'Daily Codex needs a connection.', pending: false };
  if (journey.safety.mode === 'journey-passive')
    return { message: 'Daily Codex waits while Journey Mode is passive.', pending: false };
  if (resultMessage?.entry === entry)
    return { message: resultMessage.text, pending: false, error: resultMessage.error };
  return {
    message: 'Opening today’s Daily Codex · no GPS needed…',
    pending: snapshot.decode.status === 'solved',
  };
}

export function useDailyDecodeEntry(snapshot: PlayerVerticalSliceView | undefined): string {
  return useDailyDecodeEntryState(snapshot).message;
}
